import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { POST } from '../../config/api';
import { setTokens } from '../../helpers/tokenStorage';
import { routes } from '../../config/config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import styles from './ForgotPasswordScreen.module.css';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [holdSixtySeconds, setHoldSixtySeconds] = useState(60);
  const [submitting, setSubmitting] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');

  const disableVerificationCode = !emailRegex.test(email) || email.length < 5 || codeSent;
  const disableResetPassword = !emailRegex.test(email) || code.length !== 4 || resetPassword.length < 5;

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (disableVerificationCode || codeSent || submitting) return;

    setCodeSent(true);
    setError('');
    setHoldSixtySeconds(60);

    // Start timer countdown
    timerRef.current = setInterval(() => {
      setHoldSixtySeconds((prev) => {
        if (prev <= 1) {
          setCodeSent(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    const response = await POST('/auth/forgot-password', {}, { email: email.trim() });

    if (response.status === 200) {
      setError('');
      // Success message could be shown here, but using inline error for consistency
    } else if (response.status === 404) {
      setCodeSent(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setHoldSixtySeconds(60);
      setError('Email not found. Please sign up.');
      // Navigate to signup with email after a short delay
      setTimeout(() => {
        navigate(routes.signup, { state: { email: email.trim() } });
      }, 2000);
    } else {
      setCodeSent(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setHoldSixtySeconds(60);
      setError(response.message || 'Something went wrong. Please try again.');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (disableResetPassword || submitting) return;

    setSubmitting(true);
    setError('');

    const response = await POST('/auth/change-password', {}, {
      email: email.trim(),
      verificationCode: code,
      password: resetPassword,
    });

    setSubmitting(false);

    if (response.status === 200 && response.data) {
      // Successful password reset
      const { accessToken, refreshToken, user, categories } = response.data;

      // Store tokens
      await setTokens(accessToken, refreshToken);

      // Store user data in localStorage
      if (user) {
        const userData = { ...user };
        delete userData.chats;
        localStorage.setItem('tellmiz_user', JSON.stringify(userData));
        if (user.unreadMessagesCount !== undefined) {
          localStorage.setItem('tellmiz_unreadMessagesCount', user.unreadMessagesCount.toString());
        }
        if (user.chats) {
          localStorage.setItem('tellmiz_chats', JSON.stringify(user.chats));
        }
      }

      if (categories) {
        localStorage.setItem('tellmiz_categories', JSON.stringify(categories));
      }

      // Navigate to reels screen
      navigate(routes.reels);
    } else if (response.error) {
      if (response.status === 404) {
        setError('Email not found. Please sign up.');
        setTimeout(() => {
          navigate(routes.signup, { state: { email: email.trim() } });
        }, 2000);
      } else {
        setError(response.message || 'Something went wrong. Please try again.');
      }
    } else {
      setError('Something went wrong. Please try again.');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBackIcon className={styles.backIcon} />
        </button>
        <h1 className={styles.title}>Forgot Password</h1>
      </div>

      <div className={styles.content}>
        <form className={styles.form}>
          <div className={styles.inputContainer}>
            <label className={styles.inputLabel}>Email</label>
            <input
              type="email"
              className={styles.textInput}
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="email"
              required
            />
          </div>

          <button
            type="button"
            onClick={handleSendCode}
            disabled={disableVerificationCode || codeSent || submitting}
            className={`${styles.sendCodeButton} ${disableVerificationCode || codeSent || submitting ? styles.disabled : ''}`}
          >
            {codeSent ? `Resend in ${holdSixtySeconds}` : 'Send Code'}
          </button>

          {codeSent && (
            <>
              <div className={styles.inputContainer}>
                <label className={styles.inputLabel}>Verification Code</label>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder="Enter 4-digit code"
                  value={code}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setCode(value);
                  }}
                  disabled={submitting}
                  maxLength={4}
                  inputMode="numeric"
                  required
                />
              </div>

              <div className={styles.inputContainer}>
                <label className={styles.inputLabel}>New Password</label>
                <div className={styles.passwordContainer}>
                  <input
                    type={passwordVisible ? 'text' : 'password'}
                    className={styles.passwordInput}
                    placeholder="Enter new password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    disabled={submitting}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    {passwordVisible ? (
                      <VisibilityOffIcon className={styles.eyeIcon} />
                    ) : (
                      <VisibilityIcon className={styles.eyeIcon} />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={handleChangePassword}
                disabled={disableResetPassword || submitting}
                className={`${styles.resetButton} ${disableResetPassword || submitting ? styles.disabled : ''}`}
              >
                {submitting ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </>
          )}

          {error && <div className={styles.errorMessage}>{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;

